import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { Trash2, Plus, Power, X, Pencil, Gauge, Sliders, Search } from "lucide-react";
import { toast } from "sonner";

const RTP_PRESETS = [
  { id: "low_88",     rtp: 88,  label: "Low 88%",     hint: "High house edge (12%)" },
  { id: "mid_92",     rtp: 92,  label: "Mid 92%",     hint: "8% house edge" },
  { id: "high_94",    rtp: 94,  label: "High 94%",    hint: "Balanced (6%)" },
  { id: "premium_96", rtp: 96,  label: "Premium 96%", hint: "Industry default (4%)" },
  { id: "elite_97",   rtp: 97,  label: "Elite 97%",   hint: "Generous (3%)" },
];

function rtpBadgeColor(rtp) {
  if (rtp >= 96) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (rtp >= 94) return "bg-yellow-50 text-yellow-800 border-yellow-200";
  if (rtp >= 92) return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-rose-50 text-rose-800 border-rose-200";
}

const TYPE_FILTERS = ["", "Slots", "Casino", "Crash", "Table", "Fishing", "Arcade", "Lottery", "Sports", "Live"];

// Multi-category options shown as checkboxes in the Add/Edit Game form.
// Slug = persisted lowercase value (matches frontend tab id); label = admin-facing.
const CATEGORY_OPTIONS = [
  // Home tabs (shown on the homepage)
  { slug: "popular", label: "Popular (Home)", group: "Home" },
  { slug: "exclusive", label: "Exclusive Games", group: "Home" },
  { slug: "hot-slots", label: "Hot Slots", group: "Home" },
  { slug: "hot-casino", label: "Hot Casino", group: "Home" },
  { slug: "new-games", label: "New Games", group: "Home" },
  // Site categories
  { slug: "sports", label: "Sports", group: "Categories" },
  { slug: "casino", label: "Casino", group: "Categories" },
  { slug: "slots", label: "Slots", group: "Categories" },
  { slug: "crash", label: "Crash", group: "Categories" },
  { slug: "table", label: "Table", group: "Categories" },
  { slug: "fishing", label: "Fishing", group: "Categories" },
  { slug: "arcade", label: "Arcade", group: "Categories" },
  { slug: "lottery", label: "Lottery", group: "Categories" },
  { slug: "live", label: "Live", group: "Categories" },
];

const EMPTY_FORM = {
  name: "",
  type: "Casino",
  categories: [],
  provider: "JILI",
  game_uid: "",
  provider_id: "",
  description: "",
  badge: "",
  order: 0,
  image: "",
  status: "active",
  min_bet: 0,
  max_bet: 0,
  rtp: 96,
  maintenance_mode: false,
  maintenance_message: "This game is under maintenance. Please try again later.",
};

export default function Games() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, or null for create
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState("Slots");
  const [search, setSearch] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleteForm, setBulkDeleteForm] = useState({ provider: "", type: "", exact: false });
  const [bulkDeleteDryRun, setBulkDeleteDryRun] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [quickRtpFor, setQuickRtpFor] = useState(null); // { id, name, rtp }
  const [summary, setSummary] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const { data } = await api.get(`/games${params}`);
      setRows(data);
    } catch (_) {
      /* interceptor handles 401 */
    } finally {
      setLoading(false);
    }
  };
  const loadSummary = async () => {
    try {
      const { data } = await api.get("/games/rtp-summary");
      setSummary(data);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => { loadSummary(); }, []);

  const filteredRows = useMemo(() => {
    if (!typeFilter) return rows;
    return rows.filter((r) => (r.type || "") === typeFilter);
  }, [rows, typeFilter]);

  const counts = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.type || "Other"] = (m[r.type || "Other"] || 0) + 1; });
    return m;
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      type: row.type || "Casino",
      categories: Array.isArray(row.categories) ? row.categories : [],
      provider: row.provider || "",
      game_uid: row.game_uid || "",
      provider_id: row.provider_id || "",
      description: row.description || "",
      badge: row.badge || "",
      order: typeof row.order === "number" ? row.order : 0,
      image: row.image || "",
      status: row.status || "active",
      min_bet: typeof row.min_bet === "number" ? row.min_bet : 0,
      max_bet: typeof row.max_bet === "number" ? row.max_bet : 0,
      rtp: typeof row.rtp === "number" ? row.rtp : 96,
      maintenance_mode: !!row.maintenance_mode,
      maintenance_message: row.maintenance_message || "This game is under maintenance. Please try again later.",
    });
    setShowForm(true);
  };

  const close = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const toggleStatus = async (row) => {
    const newStatus = row.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/games/${row.id}`, { status: newStatus });
      toast.success(`Game ${newStatus === "active" ? "enabled" : "disabled"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        min_bet: Number(form.min_bet) || 0,
        max_bet: Number(form.max_bet) || 0,
        rtp: Number(form.rtp) || 0,
        maintenance_mode: !!form.maintenance_mode,
      };
      if (editing) {
        await api.patch(`/games/${editing.id}`, payload);
        toast.success("Game updated");
      } else {
        await api.post("/games", payload);
        toast.success("Game created");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? "Update failed" : "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete game "${row.name}"?`)) return;
    try {
      await api.delete(`/games/${row.id}`);
      toast.success("Game deleted");
      load();
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const quickSaveRtp = async (gameId, rtp) => {
    try {
      await api.patch(`/games/${gameId}`, { rtp: Number(rtp) });
      toast.success(`RTP set to ${rtp}%`);
      setQuickRtpFor(null);
      load();
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const bulkDeleteDryRunFetch = async () => {
    if (!bulkDeleteForm.provider && !bulkDeleteForm.type) {
      toast.error("Enter at least a provider or type filter");
      return;
    }
    try {
      const { data } = await api.post("/games/bulk-delete-by-filter", {
        provider: bulkDeleteForm.provider || null,
        type: bulkDeleteForm.type || null,
        provider_exact: !!bulkDeleteForm.exact,
        confirm: false,
      });
      setBulkDeleteDryRun(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    }
  };

  const bulkDeleteConfirm = async () => {
    if (!bulkDeleteDryRun || !bulkDeleteDryRun.would_delete) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/games/bulk-delete-by-filter", {
        provider: bulkDeleteForm.provider || null,
        type: bulkDeleteForm.type || null,
        provider_exact: !!bulkDeleteForm.exact,
        confirm: true,
      });
      toast.success(`Deleted ${data.deleted} games`);
      setShowBulkDelete(false);
      setBulkDeleteForm({ provider: "", type: "", exact: false });
      setBulkDeleteDryRun(null);
      load();
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div data-testid="games-page">
      <PageHeader
        title="Games"
        subtitle="Per-game RTP control: filter by category (default Slots), tweak each game's RTP individually with the Quick RTP popover, or bulk-apply across all games of a type. Set the provider Game UID to wire it up to the DST API."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkDelete(true)}
              className="h-9 px-3 bg-rose-500 hover:bg-rose-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5"
              data-testid="game-bulk-delete-btn"
              title="Delete all games from a specific provider"
            >
              <Trash2 size={14} /> Bulk Delete
            </button>
            <button
              onClick={() => setShowBulk(true)}
              className="h-9 px-3 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5"
              data-testid="game-bulk-rtp-btn"
            >
              <Sliders size={14} /> Bulk RTP
            </button>
            <button
              onClick={openCreate}
              className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5"
              data-testid="game-add-btn"
            >
              <Plus size={14} /> Add game
            </button>
          </div>
        }
      />

      <GameRtpSummaryBanner summary={summary} />

      {/* Filter + search bar */}
      <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-col md:flex-row gap-3 md:items-center" data-testid="games-filter-bar">
        <div className="flex flex-wrap gap-1.5 flex-1" data-testid="games-type-chips">
          {TYPE_FILTERS.map((t) => {
            const label = t || "All";
            const active = typeFilter === t;
            const count = t ? (counts[t] || 0) : rows.length;
            return (
              <button
                key={t || "all"}
                onClick={() => setTypeFilter(t)}
                className={`h-8 px-2.5 text-xs rounded-sm border ${
                  active ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
                data-testid={`games-type-${t || "all"}`}
              >
                {label} {count ? <span className={active ? "opacity-80" : "text-gray-400"}>({count})</span> : null}
              </button>
            );
          })}
        </div>
        <div className="relative flex-shrink-0 md:w-72">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search game name / provider…"
            className="w-full h-9 pl-8 pr-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
            data-testid="games-search-input"
          />
        </div>
      </div>

      <DataTable
        testId="games-table"
        loading={loading}
        rows={filteredRows}
        columns={[
          {
            key: "image",
            label: "Image",
            render: (r) =>
              r.image ? (
                <img src={r.image} alt={r.name} className="h-10 w-14 object-cover rounded-sm" />
              ) : (
                <div className="h-10 w-14 bg-gray-100 rounded-sm flex items-center justify-center text-xs text-gray-400">
                  —
                </div>
              ),
          },
          { key: "name", label: "Name" },
          { key: "type", label: "Type" },
          { key: "provider", label: "Provider" },
          {
            key: "game_uid",
            label: "Game UID",
            render: (r) => (
              <span className="font-mono text-xs text-gray-700" data-testid={`game-uid-${r.id}`}>
                {r.game_uid || <span className="text-gray-400">—</span>}
              </span>
            ),
          },
          {
            key: "rtp",
            label: "RTP",
            render: (r) => {
              const rtp = typeof r.rtp === "number" ? r.rtp : 96;
              return (
                <span
                  className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] font-semibold border rounded-sm tabular-nums ${rtpBadgeColor(rtp)}`}
                  data-testid={`game-rtp-cell-${r.id}`}
                >
                  <Gauge size={10} /> {rtp.toFixed(1)}%
                </span>
              );
            },
          },
          {
            key: "status",
            label: "Status",
            render: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge>,
          },
        ]}
        actions={(row) => (
          <div className="flex items-center gap-1 justify-end">
            <button
              onClick={() => setQuickRtpFor({ id: row.id, name: row.name, rtp: typeof row.rtp === "number" ? row.rtp : 96 })}
              className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-indigo-200 text-indigo-700 rounded-sm hover:bg-indigo-50"
              data-testid={`game-quick-rtp-${row.id}`}
              title="Quick RTP edit"
            >
              <Gauge size={12} /> RTP
            </button>
            <button
              onClick={() => openEdit(row)}
              className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 rounded-sm text-gray-700 hover:bg-gray-50"
              data-testid={`game-edit-${row.id}`}
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              onClick={() => toggleStatus(row)}
              className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${
                row.status === "active"
                  ? "border-amber-200 text-amber-700 hover:bg-amber-50"
                  : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              }`}
              data-testid={`game-toggle-${row.id}`}
            >
              <Power size={12} /> {row.status === "active" ? "Disable" : "Enable"}
            </button>
            <button
              onClick={() => onDelete(row)}
              className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200"
              data-testid={`game-delete-${row.id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      />

      {showForm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="bg-white rounded-sm w-full max-w-xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="font-display font-bold">{editing ? "Edit game" : "Add game"}</h3>
              <button onClick={close} className="h-8 w-8 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3" data-testid="game-form">
              <Field
                label="Name *"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testId="game-name"
                required
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Primary Type (legacy)
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                    data-testid="game-type"
                  >
                    <optgroup label="Home shelves">
                      <option value="Popular">Popular (Home featured)</option>
                      <option value="Exclusive">Exclusive Games (carousel)</option>
                      <option value="Hot Slots">Hot Slots (carousel)</option>
                      <option value="Hot Casino">Hot Casino (carousel)</option>
                      <option value="New Games">New Games (carousel)</option>
                    </optgroup>
                    <optgroup label="Categories">
                      <option value="Sports">Sports</option>
                      <option value="Casino">Casino</option>
                      <option value="Slots">Slots</option>
                      <option value="Crash">Crash</option>
                      <option value="Table">Table</option>
                      <option value="Fishing">Fishing</option>
                      <option value="Arcade">Arcade</option>
                      <option value="Lottery">Lottery</option>
                      <option value="Live">Live</option>
                    </optgroup>
                  </select>
                </div>
                <Field
                  label="Provider"
                  value={form.provider}
                  onChange={(v) => setForm({ ...form, provider: v })}
                  testId="game-provider"
                  placeholder="JILI, PG Soft, Spribe…"
                />
              </div>

              {/* Multi-category — a single game can appear in many tabs */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                    Show in categories <span className="text-gray-400 normal-case font-normal">(check all that apply — game appears in every checked tab)</span>
                  </label>
                  <span className="text-[11px] text-gray-500" data-testid="game-categories-count">
                    {form.categories.length} selected
                  </span>
                </div>
                {["Home", "Categories"].map((group) => (
                  <div key={group} className="mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{group}</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`game-categories-${group.toLowerCase()}`}>
                      {CATEGORY_OPTIONS.filter((o) => o.group === group).map((opt) => {
                        const checked = form.categories.includes(opt.slug);
                        return (
                          <label
                            key={opt.slug}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs cursor-pointer select-none ${
                              checked
                                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                            }`}
                            data-testid={`game-category-${opt.slug}`}
                          >
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 accent-emerald-600"
                              checked={checked}
                              onChange={(e) => {
                                const next = new Set(form.categories);
                                if (e.target.checked) next.add(opt.slug);
                                else next.delete(opt.slug);
                                setForm({ ...form, categories: Array.from(next) });
                              }}
                            />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Game UID (Provider)"
                  value={form.game_uid}
                  onChange={(v) => setForm({ ...form, game_uid: v })}
                  testId="game-uid"
                  placeholder="e.g. 3978"
                  hint="The provider's unique game id (SoftAPI / JILI game_uid)"
                  mono
                />
                <Field
                  label="Provider ID (internal)"
                  value={form.provider_id}
                  onChange={(v) => setForm({ ...form, provider_id: v })}
                  testId="game-provider-id"
                  placeholder="optional"
                  mono
                />
              </div>

              <Field
                label="Image URL"
                value={form.image}
                onChange={(v) => setForm({ ...form, image: v })}
                testId="game-image"
                placeholder="https://…"
              />

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Badge
                  </label>
                  <select
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                    className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                    data-testid="game-badge"
                  >
                    <option value="">— none —</option>
                    <option value="Hot">Hot</option>
                    <option value="New">New</option>
                    <option value="Top">Top</option>
                  </select>
                </div>
                <Field
                  label="Order"
                  type="number"
                  value={form.order}
                  onChange={(v) => setForm({ ...form, order: v })}
                  testId="game-order"
                />
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                    data-testid="game-status"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="game-description"
                  placeholder="Optional short description shown on hover."
                />
              </div>

              <div className="pt-2 border-t border-gray-100">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Advanced controls</div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Min bet (৳)" type="number" value={form.min_bet} onChange={(v) => setForm({ ...form, min_bet: v })} testId="game-min-bet" />
                  <Field label="Max bet (৳)" type="number" value={form.max_bet} onChange={(v) => setForm({ ...form, max_bet: v })} testId="game-max-bet" />
                  <Field label="RTP %" type="number" value={form.rtp} onChange={(v) => setForm({ ...form, rtp: v })} testId="game-rtp" hint="Return-to-player percentage" />
                </div>
                <label className="flex items-center gap-3 mt-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={!!form.maintenance_mode} onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })} data-testid="game-maintenance" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Maintenance mode (block launch)</div>
                    <div className="text-xs text-gray-500">Users trying to launch this game will see the message below.</div>
                  </div>
                </label>
                {form.maintenance_mode && (
                  <div className="mt-2">
                    <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Maintenance message</label>
                    <textarea value={form.maintenance_message} onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" data-testid="game-maintenance-message" />
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold rounded-sm text-sm"
                data-testid="game-submit"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create game"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showBulk && (
        <GameBulkRtpModal
          rows={rows}
          onClose={() => setShowBulk(false)}
          afterApply={() => { setShowBulk(false); load(); loadSummary(); }}
        />
      )}

      {showBulkDelete && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => { if (!bulkDeleting) { setShowBulkDelete(false); setBulkDeleteDryRun(null); } }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 520, padding: 22, borderRadius: 14, background: "#fff", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={18} className="text-rose-600" />
              <div className="font-black text-gray-900 text-lg">Bulk Delete Games</div>
            </div>
            <p className="text-sm text-gray-600 mb-4">Delete all games from a specific provider and/or category. This action <b className="text-rose-600">cannot be undone</b>.</p>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Provider (case-insensitive)</label>
                <input
                  value={bulkDeleteForm.provider}
                  onChange={(e) => { setBulkDeleteForm({ ...bulkDeleteForm, provider: e.target.value }); setBulkDeleteDryRun(null); }}
                  placeholder="e.g. JDB"
                  className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-rose-500"
                  data-testid="bulk-delete-provider"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Type (optional)</label>
                <select
                  value={bulkDeleteForm.type}
                  onChange={(e) => { setBulkDeleteForm({ ...bulkDeleteForm, type: e.target.value }); setBulkDeleteDryRun(null); }}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-rose-500"
                  data-testid="bulk-delete-type"
                >
                  <option value="">All types</option>
                  <option value="Slots">Slots</option>
                  <option value="Casino">Casino</option>
                  <option value="Live Casino">Live Casino</option>
                  <option value="Crash">Crash</option>
                  <option value="Table">Table</option>
                  <option value="Fishing">Fishing</option>
                  <option value="Arcade">Arcade</option>
                  <option value="Lottery">Lottery</option>
                  <option value="Sports">Sports</option>
                  <option value="Live">Live</option>
                </select>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkDeleteForm.exact}
                  onChange={(e) => { setBulkDeleteForm({ ...bulkDeleteForm, exact: e.target.checked }); setBulkDeleteDryRun(null); }}
                  data-testid="bulk-delete-exact"
                />
                <span className="text-xs text-gray-600">Match provider name <b>exactly</b> (otherwise substring match)</span>
              </label>
            </div>

            {!bulkDeleteDryRun ? (
              <button
                onClick={bulkDeleteDryRunFetch}
                className="w-full mt-4 h-10 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-sm text-sm inline-flex items-center justify-center gap-2"
                data-testid="bulk-delete-preview"
              >
                <Search size={14} /> Preview matching games
              </button>
            ) : (
              <div className="mt-4">
                <div className="rounded-sm border border-rose-200 bg-rose-50 p-3">
                  <div className="text-sm text-rose-900 font-bold">
                    Will delete {bulkDeleteDryRun.would_delete} game{bulkDeleteDryRun.would_delete === 1 ? "" : "s"}.
                  </div>
                  {(bulkDeleteDryRun.sample || []).length > 0 && (
                    <div className="mt-2 text-[12px] text-rose-800 max-h-32 overflow-y-auto">
                      <div className="font-semibold mb-1">Sample:</div>
                      {bulkDeleteDryRun.sample.map((g) => (
                        <div key={g.id} className="truncate">• {g.name} <span className="text-rose-600">[{g.provider}] {g.type}</span></div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => setBulkDeleteDryRun(null)}
                    disabled={bulkDeleting}
                    className="h-10 px-3 border border-gray-200 rounded-sm text-sm text-gray-700 hover:bg-gray-50"
                  >Re-preview</button>
                  <button
                    onClick={bulkDeleteConfirm}
                    disabled={bulkDeleting || !bulkDeleteDryRun.would_delete}
                    className="flex-1 h-10 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold rounded-sm text-sm inline-flex items-center justify-center gap-2"
                    data-testid="bulk-delete-confirm"
                  >
                    {bulkDeleting ? "Deleting..." : <><Trash2 size={14} /> Confirm Delete {bulkDeleteDryRun.would_delete}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {quickRtpFor && (
        <GameQuickRtpModal
          game={quickRtpFor}
          onClose={() => setQuickRtpFor(null)}
          onSave={(v) => quickSaveRtp(quickRtpFor.id, v)}
        />
      )}
    </div>
  );
}

function GameRtpSummaryBanner({ summary }) {
  if (!summary) return null;
  const { total, avg_rtp, by_type, buckets } = summary;
  const slots = by_type?.Slots;
  return (
    <div className="bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border border-indigo-200 rounded-sm p-4 mb-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="games-rtp-summary-banner">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-sm bg-indigo-500 text-white inline-flex items-center justify-center">
          <Gauge size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">All Games Avg. RTP</div>
          <div className="font-display text-xl font-bold tabular text-gray-900">{(avg_rtp || 0).toFixed(2)}%</div>
        </div>
      </div>
      <div className="h-10 w-px bg-gray-200" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Total games</div>
        <div className="font-display text-base font-bold text-gray-800">
          {total} <span className="text-xs text-gray-500 font-normal">indexed</span>
        </div>
      </div>
      {slots && (
        <>
          <div className="h-10 w-px bg-gray-200" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Slots Avg.</div>
            <span className={`inline-flex items-center gap-1 h-7 px-2 mt-0.5 text-xs font-bold border rounded-sm ${rtpBadgeColor(slots.avg_rtp)}`}>
              <Gauge size={11} /> {slots.avg_rtp.toFixed(1)}% <span className="font-normal text-gray-500">· {slots.count}</span>
            </span>
          </div>
        </>
      )}
      <div className="h-10 w-px bg-gray-200" />
      <div className="flex flex-wrap gap-1.5 flex-1 min-w-[280px]">
        {Object.entries(buckets || {}).map(([range, n]) => {
          const mid = parseFloat(range.split("-")[0]) + 1;
          return (
            <span
              key={range}
              className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] rounded-sm border ${rtpBadgeColor(mid)}`}
              title={`${n} game(s) with RTP ${range}%`}
            >
              <span className="font-semibold tabular-nums">{range}%</span>
              <span className="text-gray-500">·{n}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function GameQuickRtpModal({ game, onClose, onSave }) {
  const [rtp, setRtp] = useState(typeof game.rtp === "number" ? game.rtp : 96);
  const [busy, setBusy] = useState(false);
  const apply = async (v) => {
    setBusy(true);
    try {
      await onSave(v);
    } finally {
      setBusy(false);
    }
  };
  const houseEdge = (100 - rtp).toFixed(2);
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="game-quick-rtp-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="font-display font-bold inline-flex items-center gap-1.5 text-base">
              <Gauge size={16} className="text-indigo-500" /> Quick RTP edit
            </h3>
            <div className="text-xs text-gray-500 mt-0.5">{game.name}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex flex-wrap gap-1.5" data-testid="game-quick-rtp-presets">
            {RTP_PRESETS.map((p) => {
              const active = rtp === p.rtp;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setRtp(p.rtp)}
                  title={p.hint}
                  data-testid={`game-quick-rtp-preset-${p.id}`}
                  className={`h-8 px-2.5 text-[11px] rounded-sm border font-semibold inline-flex items-center gap-1 ${
                    active
                      ? rtpBadgeColor(p.rtp) + " ring-2 ring-offset-1 ring-indigo-300"
                      : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Gauge size={10} /> {p.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-[1fr_auto] items-center gap-3">
            <input
              type="range"
              min="80"
              max="99"
              step="0.5"
              value={rtp}
              onChange={(e) => setRtp(Number(e.target.value))}
              className="w-full accent-indigo-500"
              data-testid="game-quick-rtp-slider"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="80"
                max="99"
                step="0.1"
                value={rtp}
                onChange={(e) => setRtp(Number(e.target.value))}
                className="w-20 h-9 px-2 border border-gray-200 rounded-sm text-sm font-mono text-right focus:outline-none focus:border-indigo-500"
                data-testid="game-quick-rtp-input"
              />
              <span className="text-sm text-gray-500 font-semibold">%</span>
            </div>
          </div>
          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
            <div className="text-xs text-gray-600">
              Player payout: <b className="text-emerald-700">{Number(rtp).toFixed(2)}%</b> · House edge: <b className="text-rose-700">{houseEdge}%</b>
            </div>
            <span className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] rounded-sm border ${rtpBadgeColor(rtp)}`}>
              <Gauge size={10} /> {Number(rtp).toFixed(1)}%
            </span>
          </div>
          <button
            type="button"
            onClick={() => apply(rtp)}
            disabled={busy || rtp < 80 || rtp > 99}
            data-testid="game-quick-rtp-save"
            className="w-full h-10 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-semibold rounded-sm text-sm inline-flex items-center justify-center gap-2"
          >
            {busy ? "Saving…" : <><Gauge size={14} /> Save RTP {Number(rtp).toFixed(1)}%</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function GameBulkRtpModal({ rows, onClose, afterApply }) {
  const [typeFilter, setTypeFilter] = useState("Slots");
  const [providerFilter, setProviderFilter] = useState("");
  const [rtp, setRtp] = useState(92);
  const [busy, setBusy] = useState(false);

  // Compute matching game count for live preview
  const matched = useMemo(() => {
    return rows.filter((r) =>
      (!typeFilter || (r.type || "") === typeFilter) &&
      (!providerFilter || (r.provider || "") === providerFilter)
    ).length;
  }, [rows, typeFilter, providerFilter]);

  const providerOptions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.provider) s.add(r.provider); });
    return Array.from(s).sort();
  }, [rows]);

  const apply = async () => {
    if (!window.confirm(`Apply ${rtp}% RTP to ${matched} game${matched === 1 ? "" : "s"}?\nThis overrides each game's current RTP.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/games/bulk-rtp", {
        type_filter: typeFilter || null,
        provider_filter: providerFilter || null,
        target_rtp: rtp,
      });
      toast.success(`Applied ${rtp}% to ${data.modified} game${data.modified === 1 ? "" : "s"}`);
      afterApply?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk apply failed");
    } finally {
      setBusy(false);
    }
  };

  const houseEdge = (100 - rtp).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="game-bulk-rtp-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-display font-bold inline-flex items-center gap-1.5">
            <Sliders size={16} className="text-indigo-500" /> Bulk apply RTP to games
          </h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-gray-600 leading-relaxed bg-indigo-50 border border-indigo-100 rounded-sm p-3">
            Apply the same RTP to many games at once — narrow scope by type and / or provider. The matching count below updates live so you can verify before applying.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                data-testid="game-bulk-rtp-type"
              >
                <option value="">All types</option>
                {TYPE_FILTERS.filter((t) => t).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Provider</label>
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                data-testid="game-bulk-rtp-provider"
              >
                <option value="">All providers</option>
                {providerOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="game-bulk-rtp-matched">
            Will affect <b className="text-amber-900 tabular-nums">{matched}</b> game{matched === 1 ? "" : "s"} matching the selected scope.
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Preset</label>
            <div className="flex flex-wrap gap-1.5" data-testid="game-bulk-rtp-presets">
              {RTP_PRESETS.map((p) => {
                const active = rtp === p.rtp;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setRtp(p.rtp)}
                    title={p.hint}
                    data-testid={`game-bulk-rtp-preset-${p.id}`}
                    className={`h-9 px-3 text-xs rounded-sm border font-semibold inline-flex items-center gap-1.5 ${
                      active
                        ? rtpBadgeColor(p.rtp) + " ring-2 ring-offset-1 ring-indigo-300"
                        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <Gauge size={11} /> {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Target RTP %</label>
            <div className="grid grid-cols-[1fr_auto] items-center gap-3">
              <input
                type="range"
                min="80"
                max="99"
                step="0.5"
                value={rtp}
                onChange={(e) => setRtp(Number(e.target.value))}
                className="w-full accent-indigo-500"
                data-testid="game-bulk-rtp-slider"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="80"
                  max="99"
                  step="0.1"
                  value={rtp}
                  onChange={(e) => setRtp(Number(e.target.value))}
                  className="w-20 h-9 px-2 border border-gray-200 rounded-sm text-sm font-mono text-right"
                  data-testid="game-bulk-rtp-input"
                />
                <span className="text-sm text-gray-500 font-semibold">%</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
            <div className="text-xs text-gray-600">
              Payout: <b className="text-emerald-700">{rtp.toFixed(2)}%</b> · House edge: <b className="text-rose-700">{houseEdge}%</b>
            </div>
            <span className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] rounded-sm border ${rtpBadgeColor(rtp)}`}>
              <Gauge size={10} /> {rtp.toFixed(1)}%
            </span>
          </div>

          <button
            type="button"
            onClick={apply}
            disabled={busy || matched === 0}
            data-testid="game-bulk-rtp-apply"
            className="w-full h-10 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-semibold rounded-sm text-sm inline-flex items-center justify-center gap-2"
          >
            {busy ? "Applying…" : <><Sliders size={14} /> Apply to {matched} game{matched === 1 ? "" : "s"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required, placeholder, hint, mono }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className={`w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 ${
          mono ? "font-mono" : ""
        }`}
        data-testid={testId}
      />
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}
